import { useState, useMemo } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { UserList } from '../components/UserList';
import { UserForm } from '../components/UserForm';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import { deleteUser } from '../api/users';
import type { UserDto } from '../types';

export function UsersPage() {
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);

  const { hasPermission } = useAuth();
  const canCreateUsers = hasPermission(Permissions.UsersCreate);
  const canUpdateUsers = hasPermission(Permissions.UsersUpdate);

  // Fetch all users or only active based on toggle
  const { data: users, isLoading, error, refetch } = useUsers(
    showInactive ? undefined : { isActive: true }
  );

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(query) ||
        user.username.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.roleName.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  const handleEditUser = (user: UserDto) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleDeactivateUser = async (user: UserDto) => {
    if (!window.confirm(`Are you sure you want to deactivate "${user.fullName}"?`)) {
      return;
    }

    try {
      await deleteUser(user.id);
      refetch();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const handleFormSuccess = () => {
    refetch();
  };

  return (
    <div className="min-h-screen bg-white px-5 py-6 space-y-4">
      {/* Header */}
      <div className="bg-white rounded-[10px] p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left side: Title */}
          <h2 className="text-lg font-semibold text-[#0a0a9e]">Users</h2>

          {/* Right side: Search, Show Inactive, Add Button */}
          <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-[250px]"
              />
            </div>

            {/* Show Inactive Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showInactive"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <Label htmlFor="showInactive" className="text-sm font-normal cursor-pointer">
                Show inactive
              </Label>
            </div>

            {/* Add Button - requires create permission */}
            {canCreateUsers && (
              <Button onClick={handleAddUser} className="gap-2">
                <Plus className="h-4 w-4" />
                Add User
              </Button>
            )}
          </div>
        </div>
      </div>

      <ErrorAlert error={error} className="rounded-[20px] p-4" />

      {/* User List */}
      <div className="bg-white rounded-[20px] p-4 border border-[#d9d9d9]">
        <UserList
          users={filteredUsers}
          onEdit={handleEditUser}
          onDeactivate={handleDeactivateUser}
          isLoading={isLoading}
          canEdit={canUpdateUsers}
        />
      </div>

      {/* Add/Edit User Form Dialog */}
      <UserForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        user={selectedUser}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
